// MessageCenter — full-height drawer inbox for the home page.
//
// Slides in from the right. Provides a complete notification inbox with
// source-based tab filtering and footer actions.
//
// Account-ready: the drawer reads from the same Zustand notification slice
// as NotificationCenter. When the remote account system lands, both
// components keep working — only the data source (store → INotificationService)
// changes.

import { useCallback, useEffect, useState } from 'react';
import { Bell, X, CheckCheck, Trash2, Check, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import { useStore } from '../lib/store/index.js';
import { useT, useLocale } from '../i18n/index.js';
import { formatRelativeTime } from '../lib/relative-time.js';
import { useNavigate } from 'react-router-dom';
import type { AppNotification, MessageFilter } from '@examforge/shared';

// ── Level icon map ───────────────────────────────────────────────────────

const LEVEL_ICON = {
  success: Check,
  info: Info,
  warning: AlertTriangle,
  error: AlertOctagon,
} as const;

// ── Tab definitions ──────────────────────────────────────────────────────

interface TabSpec {
  key: MessageFilter;
  labelKey: string;
}

const TABS: readonly TabSpec[] = [
  { key: 'all', labelKey: 'notif.tabAll' },
  { key: 'regen', labelKey: 'notif.tabStudy' },
  { key: 'system', labelKey: 'notif.tabSystem' },
  { key: 'account', labelKey: 'notif.tabAccount' },
];

// ── Relative time ────────────────────────────────────────────────────────
// 19BM-D Phase 6b: extracted to lib/relative-time.ts (was duplicated in
// message-center / notification-panel / history-view).

// ── Props ────────────────────────────────────────────────────────────────

export interface MessageCenterProps {
  open: boolean;
  onClose: () => void;
}

// ── Component ────────────────────────────────────────────────────────────

export function MessageCenter({ open, onClose }: MessageCenterProps) {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();

  const notifications = useStore((s) => s.notifications);
  const markRead = useStore((s) => s.markRead);
  const markAllRead = useStore((s) => s.markAllRead);
  const dismiss = useStore((s) => s.dismiss);
  const clearAll = useStore((s) => s.clearAll);

  const [activeTab, setActiveTab] = useState<MessageFilter>('all');

  // ── Esc closes ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // ── Filter ──────────────────────────────────────────────────────────
  const filtered = activeTab === 'all'
    ? notifications
    : activeTab === 'regen'
      ? notifications.filter((n) => n.source === 'regen' || n.source === 'ingest')
      : notifications.filter((n) => n.source === activeTab);

  const handleAction = useCallback(
    (n: AppNotification) => {
      markRead(n.id);
      if (n.action?.onClick) {
        n.action.onClick();
      } else if (n.action?.to) {
        navigate(n.action.to);
      }
      onClose();
    },
    [markRead, navigate, onClose],
  );

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div className="message-center-overlay" onClick={onClose} aria-hidden="true" />
      {/* Drawer */}
      <aside className="message-center-drawer" role="dialog" aria-label={t('notif.messageCenter')}>
        {/* Header */}
        <div className="message-center-header">
          <h2 className="message-center-title">{t('notif.messageCenter')}</h2>
          <button
            type="button"
            className="message-center-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="message-center-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`message-center-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {t(tab.labelKey as never)}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="message-center-body">
          {filtered.length === 0 ? (
            <div className="message-center-empty">
              <Bell size={32} aria-hidden="true" className="message-center-empty-icon" />
              <span className="message-center-empty-text">
                {t(activeTab === 'all' ? 'notif.empty' : 'notif.emptyFiltered')}
              </span>
              {activeTab === 'all' && (
                <span className="message-center-empty-hint">{t('notif.emptyHint')}</span>
              )}
              {activeTab !== 'all' && (
                <button
                  type="button"
                  className="message-center-empty-reset"
                  onClick={() => setActiveTab('all')}
                >
                  {t('notif.viewAll')}
                </button>
              )}
            </div>
          ) : (
            <ul className="message-center-list">
              {filtered.map((n) => (
                <li
                  key={n.id}
                  className={`message-center-item${n.read ? '' : ' message-center-item--unread'} message-center-item--${n.level}`}
                  onClick={() => {
                    if (!n.read) markRead(n.id);
                  }}
                >
                  <span className="message-center-item-icon" aria-hidden="true">
                    {(() => {
                      const Icon = LEVEL_ICON[n.level];
                      return <Icon size={14} aria-hidden="true" />;
                    })()}
                  </span>
                  <div className="message-center-item-body">
                    <div className="message-center-item-head">
                      <span className="message-center-item-title">{n.title}</span>
                      <span className="message-center-item-time">
                        {formatRelativeTime(n.timestamp, locale, t)}
                      </span>
                    </div>
                    {n.message && (
                      <span className="message-center-item-msg">{n.message}</span>
                    )}
                    <span className="message-center-item-source">
                      {TABS.find((tab) => tab.key === n.source || (tab.key === 'regen' && n.source === 'ingest'))
                        ? t(TABS.find((tab) => tab.key === n.source || (tab.key === 'regen' && n.source === 'ingest'))!.labelKey as never)
                        : n.source}
                    </span>
                  </div>
                  <div className="message-center-item-tail">
                    {n.action && (
                      <button
                        type="button"
                        className="message-center-item-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction(n);
                        }}
                      >
                        {n.action.label}
                      </button>
                    )}
                    <button
                      type="button"
                      className="message-center-item-dismiss-btn"
                      aria-label={t('common.close')}
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss(n.id);
                      }}
                    >
                      <X size={13} aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="message-center-footer">
          {notifications.length > 0 && (
            <>
              <button
                type="button"
                className="message-center-footer-btn"
                onClick={markAllRead}
                disabled={notifications.every((n) => n.read)}
              >
                <CheckCheck size={14} aria-hidden="true" />
                {t('notif.markAllRead')}
              </button>
              <button
                type="button"
                className="message-center-footer-btn message-center-footer-btn--danger"
                onClick={clearAll}
              >
                <Trash2 size={14} aria-hidden="true" />
                {t('notif.clearAll')}
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
